import { useEffect, useRef } from "react";
import {
  sendbirdSelectors,
  useSendbirdStateContext,
} from "@sendbird/uikit-react";
import { getFCMToken } from "../../firebase";

const PUSH_TRIGGER_ALL = "all";

/**
 * Marks this tab offline as soon as it unloads so Sendbird can send FCM.
 * Does not run on visibility hidden — that would drop the WebSocket and
 * break in-app / background-tab toasts.
 *
 * @param {object} sdk
 * @param {boolean} isSdkInitialized
 */
const useSendbirdOfflineOnTabClose = (sdk, isSdkInitialized) => {
  useEffect(() => {
    if (
      !isSdkInitialized ||
      (typeof sdk?.setBackgroundState !== "function" &&
        typeof sdk?.disconnectWebSocket !== "function")
    ) {
      return undefined;
    }

    const goBackground = () => {
      try {
        if (typeof sdk.setBackgroundState === "function") {
          sdk.setBackgroundState();
          return;
        }
        sdk.disconnectWebSocket();
      } catch (error) {
        console.error("Failed to mark Sendbird offline for closed-tab push:", error);
      }
    };

    const goForeground = () => {
      try {
        if (typeof sdk.setForegroundState === "function") {
          sdk.setForegroundState();
        }
      } catch (error) {
        console.error("Failed to restore Sendbird after tab close:", error);
      }
    };

    window.addEventListener("pagehide", goBackground);
    window.addEventListener("pageshow", goForeground);
    return () => {
      window.removeEventListener("pagehide", goBackground);
      window.removeEventListener("pageshow", goForeground);
    };
  }, [sdk, isSdkInitialized]);
};

/**
 * Registers the web FCM token with Sendbird Chat so team/patient messages
 * can wake the service worker when the app tab is closed.
 * Kept independent of Sendbird Calls auth — Calls failures must not block chat push.
 *
 * @param {NotificationPermission|""} permission
 */
const useSendbirdPushRegistration = (permission) => {
  const context = useSendbirdStateContext();
  const sdk = sendbirdSelectors.getSdk(context);
  const isSdkInitialized = Boolean(context?.stores?.sdkStore?.initialized);
  const registeredTokenRef = useRef("");
  const currentUserId = sdk?.currentUser?.userId;

  useSendbirdOfflineOnTabClose(sdk, isSdkInitialized);

  useEffect(() => {
    let cancelled = false;

    const registerToken = async () => {
      if (
        permission !== "granted" ||
        !isSdkInitialized ||
        !sdk ||
        typeof sdk.registerFCMPushTokenForCurrentUser !== "function" ||
        !currentUserId
      ) {
        return;
      }

      try {
        const token = await getFCMToken();
        if (!token || cancelled) return;
        if (registeredTokenRef.current === token) return;

        const registrationState =
          await sdk.registerFCMPushTokenForCurrentUser(token);
        const failed =
          registrationState === "error" ||
          registrationState === "ERROR" ||
          registrationState === 2;
        if (failed) {
          console.error("Sendbird FCM token registration returned error");
          return;
        }
        if (typeof sdk.setPushTriggerOption === "function") {
          await sdk.setPushTriggerOption(PUSH_TRIGGER_ALL);
        }
        registeredTokenRef.current = token;
      } catch (error) {
        console.error("Failed to register Sendbird FCM push token:", error);
      }
    };

    registerToken();

    return () => {
      cancelled = true;
    };
  }, [sdk, isSdkInitialized, currentUserId, permission]);
};

export default useSendbirdPushRegistration;
